import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Download, RefreshCw, Search, Filter, Calendar,
  Building2, Euro, Package, ChevronRight, AlertCircle, Check,
  Clock, FileWarning, ExternalLink, Info, Loader2, Settings,
  Utensils, Box, Wrench, HelpCircle, Ban, RotateCcw, CheckCircle2, AlertTriangle
} from "lucide-react";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mappa stati fattura SDI
const STATI_FATTURA = {
  NEW: { label: "Nuova", color: "bg-blue-100 text-blue-700", icon: Clock },
  RECEIVED: { label: "Ricevuta", color: "bg-yellow-100 text-yellow-700", icon: Download },
  DONE: { label: "Completata", color: "bg-green-100 text-green-700", icon: Check },
  ERROR: { label: "Errore", color: "bg-red-100 text-red-700", icon: AlertCircle }
};

// Mappa tipi documento
const TIPI_DOCUMENTO = {
  TD01: "Fattura",
  TD02: "Acconto",
  TD03: "Acconto",
  TD04: "Nota Credito",
  TD05: "Nota Debito",
  TD06: "Parcella",
  TD07: "Fattura Semplificata",
  TD08: "NC Semplificata",
  TD09: "ND Semplificata",
  TD16: "Integrazione Reverse",
  TD17: "Integrazione Servizi UE",
  TD18: "Integrazione Beni UE",
  TD19: "Integrazione Beni art.17",
  TD20: "Autofattura",
  TD21: "Autofattura Splafonamento",
  TD22: "Estrazione Beni Deposito",
  TD23: "Estrazione Beni Deposito IVA",
  TD24: "Fattura Differita art.21",
  TD25: "Fattura Differita art.21 bis",
  TD26: "Cessione Beni Ammortizzabili",
  TD27: "Autoconsumo",
  TD28: "Acquisti S.Marino",
  TD29: "Comunicazione specifica"
};

const ImportFattureSDI = ({ embedded = false }) => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sdiStatus, setSdiStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [fatture, setFatture] = useState([]);
  const [matchFornitori, setMatchFornitori] = useState({}); // invoice_id -> { fornitore_id, categoria, match_method, ignored, imported }
  const [mostraServizi, setMostraServizi] = useState(false); // default: nasconde servizi/utenze
  const [mostraIgnorati, setMostraIgnorati] = useState(false); // default: nasconde fornitori in blocklist
  const [mostraImportate, setMostraImportate] = useState(false); // default: nasconde le fatture già importate
  const [ignorandoInvoiceId, setIgnorandoInvoiceId] = useState(null); // id fattura in corso di blocklist
  const [selectedFattura, setSelectedFattura] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastCachedAt, setLastCachedAt] = useState(null); // timestamp ultimo aggiornamento cache
  
  // Filtri
  const [search, setSearch] = useState("");
  const [filtroStato, setFiltroStato] = useState("tutti");
  const [presetAttivo, setPresetAttivo] = useState("mese_corrente");

  // Helper: primo giorno del mese corrente / fine mese corrente
  const oggi = new Date();
  const defaultDa = `${oggi.getFullYear()}-${String(oggi.getMonth() + 1).padStart(2, "0")}-01`;
  const defaultA = new Date(oggi.getFullYear(), oggi.getMonth() + 1, 0).toISOString().split("T")[0];

  const [filtroDataDa, setFiltroDataDa] = useState(defaultDa);
  const [filtroDataA, setFiltroDataA] = useState(defaultA);

  // ── Cache localStorage ────────────────────────────────────────────────────
  const CACHE_TTL = 2 * 60 * 60 * 1000; // 2 ore
  const cacheKey = `sdi_fatture_cache_${localeCorrente?.id || "default"}`;

  const saveCache = useCallback((fattureData, matchData, da, a, ts) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        fatture: fattureData,
        matchFornitori: matchData,
        filtroDataDa: da,
        filtroDataA: a,
        timestamp: ts || Date.now(),
      }));
    } catch (e) {
      console.warn("Cache SDI: impossibile salvare", e);
    }
  }, [cacheKey]);

  // Normalizzazioni allineate al backend (_normalize_piva / _normalize_text)
  const normPivaJS = (s) => {
    if (!s) return "";
    let x = String(s).toUpperCase().trim().replace(/\s/g, "").replace(/\./g, "").replace(/-/g, "");
    if (x.startsWith("IT")) x = x.slice(2);
    return (x.match(/\d/g) || []).join("");
  };
  const normTextJS = (s) => {
    if (!s) return "";
    return String(s).toUpperCase()
      .replace(/\./g, " ").replace(/,/g, " ").replace(/-/g, " ")
      .replace(/S R L/g, "SRL").replace(/S P A/g, "SPA")
      .replace(/ {2}/g, " ").trim();
  };

  // Carica cache al mount (solo se locale pronto) + riconcilia col blocklist backend (fonte di verità)
  useEffect(() => {
    if (!localeCorrente?.id) return;
    let cancelled = false;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return;
      const { fatture: f, matchFornitori: m, filtroDataDa: da, filtroDataA: a, timestamp } = JSON.parse(raw);
      if (Date.now() - timestamp < CACHE_TTL && Array.isArray(f) && f.length > 0) {
        setFatture(f);
        setMatchFornitori(m || {});
        if (da) setFiltroDataDa(da);
        if (a) setFiltroDataA(a);
        setHasSearched(true);
        setLastCachedAt(timestamp);

        // Riconcilia col backend (fonte di verità): aggiorna i flag "ignored" E "imported"
        // così le fatture già importate/ignorate non riappaiono per via di una cache stale.
        (async () => {
          try {
            const payload = {
              fatture: f.map(inv => ({
                invoice_id: inv.id,
                sender_name: inv.sender?.name || null,
                sender_vat_code: inv.sender?.vat_code || null,
                document_number: inv.document_number || null,
              })),
              locale_id: localeCorrente?.id || null,
            };
            const res = await axios.post(`${API}/sdi/match-fornitori-batch`, payload, { headers: getAuthHeaders() });
            if (cancelled) return;
            const reconciled = { ...(m || {}) };
            for (const r of res.data.results || []) {
              reconciled[r.invoice_id] = { ...(reconciled[r.invoice_id] || {}), ...r };
            }
            setMatchFornitori(reconciled);
            saveCache(f, reconciled, da, a, timestamp);
          } catch (e) {
            console.warn("Cache SDI: riconciliazione backend fallita", e);
          }
        })();
      }
    } catch (e) {
      console.warn("Cache SDI: errore ripristino", e);
    }
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localeCorrente?.id]);

  // Formatta "X minuti fa"
  const formatRelativeTime = (ts) => {
    if (!ts) return "";
    const diff = Math.round((Date.now() - ts) / 60000); // minuti
    if (diff < 1) return "pochi secondi fa";
    if (diff === 1) return "1 minuto fa";
    if (diff < 60) return `${diff} minuti fa`;
    const h = Math.round(diff / 60);
    return `${h} or${h === 1 ? "a" : "e"} fa`;
  };

  // Converte "YYYY-MM" in primo/ultimo giorno del mese
  const meseToRange = (meseDa, meseA) => {
    const [yDa, mDa] = meseDa.split("-").map(Number);
    const [yA, mA] = meseA.split("-").map(Number);
    const da = `${yDa}-${String(mDa).padStart(2, "0")}-01`;
    const ultimoGiorno = new Date(yA, mA, 0).getDate();
    const a = `${yA}-${String(mA).padStart(2, "0")}-${String(ultimoGiorno).padStart(2, "0")}`;
    return { da, a };
  };

  // Applica un preset rapido
  const applicaPreset = useCallback((id) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth(); // 0-based
    let da, a;
    if (id === "mese_corrente") {
      da = new Date(y, m, 1);
      a = new Date(y, m + 1, 0);
    } else if (id === "mese_scorso") {
      da = new Date(y, m - 1, 1);
      a = new Date(y, m, 0);
    } else if (id === "trimestre") {
      da = new Date(y, m - 2, 1);
      a = new Date(y, m + 1, 0);
    } else if (id === "anno") {
      da = new Date(y, 0, 1);
      a = new Date(y, 11, 31);
    }
    setFiltroDataDa(da.toISOString().split("T")[0]);
    setFiltroDataA(a.toISOString().split("T")[0]);
    setPresetAttivo(id);
  }, []);

  // Input mese: legge "YYYY-MM" e imposta range completo
  const handleMeseDa = (val) => {
    if (!val) return;
    const meseA = filtroDataA.slice(0, 7);
    // Se il nuovo mese di partenza è dopo il mese di fine, allinea il mese di fine
    const meseAEff = val > meseA ? val : meseA;
    const { da, a } = meseToRange(val, meseAEff);
    setFiltroDataDa(da);
    setFiltroDataA(a); // sempre l'ULTIMO giorno del mese di fine (no più YYYY-MM-01)
    setPresetAttivo(null);
  };
  const handleMeseA = (val) => {
    if (!val) return;
    const meseDa = filtroDataDa.slice(0, 7);
    const { a } = meseToRange(meseDa, val);
    setFiltroDataA(a);
    setPresetAttivo(null);
  };
  
  // Dialog dettaglio
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [fatturaDetail, setFatturaDetail] = useState(null);
  
  // Paginazione
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Carica stato SDI
  const fetchSDIStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      const res = await axios.get(`${API}/sdi/status`, { headers: getAuthHeaders() });
      setSdiStatus(res.data);
    } catch (error) {
      console.error("Errore stato SDI:", error);
    } finally {
      setStatusLoading(false);
    }
  }, [getAuthHeaders]);

  // Carica fatture dal provider configurato (solo su richiesta esplicita)
  const fetchFatture = useCallback(async (showToast = true) => {
    // Se nessun provider configurato, non chiamare
    if (sdiStatus && !sdiStatus.provider) {
      setFatture([]);
      setTotal(0);
      return;
    }

    try {
      setLoading(true);
      setRefreshing(true);

      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      
      if (filtroStato !== "tutti") {
        params.append("state", filtroStato);
      }
      if (filtroDataDa) {
        params.append("from_date", filtroDataDa);
      }
      if (filtroDataA) {
        params.append("to_date", filtroDataA);
      }
      
      const res = await axios.get(`${API}/sdi/invoices?${params.toString()}`, { 
        headers: getAuthHeaders() 
      });
      
      const invoicesList = res.data.invoices || [];
      setFatture(invoicesList);
      setTotal(res.data.total || 0);
      setHasSearched(true);

      // Match fornitori → categoria (food/packaging/servizi) + importate
      let matchMap = {};
      if (invoicesList.length > 0) {
        try {
          const payload = {
            fatture: invoicesList.map(inv => ({
              invoice_id: inv.id,
              sender_name: inv.sender?.name || null,
              sender_vat_code: inv.sender?.vat_code || null,
              document_number: inv.document_number || null,
            })),
            locale_id: localeCorrente?.id || null,
          };
          const matchRes = await axios.post(
            `${API}/sdi/match-fornitori-batch`,
            payload,
            { headers: getAuthHeaders() }
          );
          for (const r of matchRes.data.results || []) {
            matchMap[r.invoice_id] = r;
          }
        } catch (e) {
          console.warn("Match fornitori batch fallito:", e);
        }
      }
      setMatchFornitori(matchMap);

      // ── AUTO-IMPORT: solo su richiesta esplicita ("Carica fatture") ──────────
      // Importa in blocco le righe con match CERTO (codice/descrizione) e prezzo
      // coerente; lascia da verificare solo nuovi/incerti/scostamento prezzo.
      if (showToast && invoicesList.length > 0) {
        const candidate = invoicesList.filter(inv => {
          const m = matchMap[inv.id];
          return m && m.fornitore_id && !m.ignored && !m.imported && !m.parziale;
        });
        if (candidate.length > 0) {
          try {
            const autoPayload = {
              locale_id: localeCorrente?.id || null,
              soglia_scostamento: 0.20,
              fatture: candidate.map(inv => ({
                invoice_id: inv.id,
                sdi_id: inv.sdi_id || null,
                document_number: inv.document_number || null,
                issue_date: inv.issue_date || null,
                sender_name: inv.sender?.name || null,
                sender_vat_code: inv.sender?.vat_code || null,
                total_amount: inv.total_amount || 0,
                lines: (inv.lines || []).map(l => ({
                  description: l.description || null,
                  quantity: l.quantity || 0,
                  unit_of_measure: l.unit_of_measure || null,
                  unit_price: l.unit_price || 0,
                  total_price: l.total_price || 0,
                  item_code: l.item_code || null,
                })),
              })),
            };
            const autoRes = await axios.post(`${API}/sdi/auto-import-batch`, autoPayload, { headers: getAuthHeaders() });
            const a = autoRes.data || {};
            // Aggiorna i flag (imported/parziale) ri-eseguendo il match batch
            try {
              const refPayload = {
                fatture: invoicesList.map(inv => ({
                  invoice_id: inv.id,
                  sender_name: inv.sender?.name || null,
                  sender_vat_code: inv.sender?.vat_code || null,
                  document_number: inv.document_number || null,
                })),
                locale_id: localeCorrente?.id || null,
              };
              const refRes = await axios.post(`${API}/sdi/match-fornitori-batch`, refPayload, { headers: getAuthHeaders() });
              matchMap = {};
              for (const r of refRes.data.results || []) matchMap[r.invoice_id] = r;
              setMatchFornitori(matchMap);
            } catch (e) { /* ignore refresh error */ }

            if (a.fatture_importate > 0) {
              toast.success(
                `Importate automaticamente ${a.fatture_importate} fattur${a.fatture_importate === 1 ? "a" : "e"} (${a.prodotti_importati} prodotti).` +
                (a.righe_da_verificare > 0 ? ` ${a.righe_da_verificare} righe da verificare a mano.` : "")
              );
            } else if (candidate.length > 0) {
              toast.info(
                `Nessun prodotto importato in automatico: i prodotti non sono ancora riconosciuti o il prezzo si discosta troppo. ${a.righe_da_verificare || 0} righe da confermare a mano.`
              );
            }
          } catch (e) {
            console.warn("Auto-import batch fallito:", e);
          }
        }
      }

      // ── Salva in cache localStorage ──────────────────────────────────────
      const now = Date.now();
      saveCache(invoicesList, matchMap, filtroDataDa, filtroDataA, now);
      setLastCachedAt(now);

      if (showToast) {
        const count = res.data.invoices?.length || 0;
        if (count === 0) {
          toast.info("Nessuna fattura trovata per il periodo selezionato");
        }
      }
    } catch (error) {
      console.error("Errore caricamento fatture:", error);
      toast.error("Errore caricamento fatture");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders, page, filtroStato, filtroDataDa, filtroDataA, sdiStatus, localeCorrente]);

  // Carica dettaglio fattura
  const fetchFatturaDetail = async (fatturaId) => {
    try {
      setDetailLoading(true);
      const res = await axios.get(`${API}/sdi/invoices/${fatturaId}`, { 
        headers: getAuthHeaders() 
      });
      setFatturaDetail(res.data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error("Errore dettaglio fattura:", error);
      toast.error("Errore caricamento dettaglio fattura");
    } finally {
      setDetailLoading(false);
    }
  };

  // Processa fattura (naviga alla pagina di revisione)
  const handleProcessFattura = (fattura) => {
    // Salva i dati della fattura nel sessionStorage per passarli alla pagina di revisione
    sessionStorage.setItem("sdi_fattura_data", JSON.stringify(fattura));
    navigate("/acquisti/revisione-fattura-sdi");
  };

  // Aggiunge il fornitore alla blocklist SDI (non mostrare più le sue fatture)
  const handleIgnoraFornitore = async (fattura, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const nomeFornitore = fattura.sender?.name || "questo fornitore";
    const ok = window.confirm(
      `Non vuoi più vedere le fatture di "${nomeFornitore}"?\n\nDa adesso saranno nascoste automaticamente dall'elenco. Potrai sempre ripristinarle tramite il toggle "Mostra fornitori ignorati".`
    );
    if (!ok) return;

    try {
      setIgnorandoInvoiceId(fattura.id);
      await axios.post(
        `${API}/sdi/fornitori-ignorati`,
        {
          piva: fattura.sender?.vat_code || null,
          nome: fattura.sender?.name || null,
          fornitore_id: matchFornitori[fattura.id]?.fornitore_id || null,
          motivo: "non importare più (dalla lista SDI)",
        },
        { headers: getAuthHeaders() }
      );
      // Aggiorna lo stato locale per nascondere subito tutte le fatture dello stesso fornitore
      const pivaTarget = (fattura.sender?.vat_code || "").trim();
      const nomeTarget = (fattura.sender?.name || "").trim().toLowerCase();
      const nextMap = { ...matchFornitori };
      for (const f of fatture) {
        const matchPiva = pivaTarget && (f.sender?.vat_code || "").trim() === pivaTarget;
        const matchNome = !pivaTarget && nomeTarget && (f.sender?.name || "").trim().toLowerCase() === nomeTarget;
        if (matchPiva || matchNome) {
          nextMap[f.id] = { ...(nextMap[f.id] || {}), ignored: true };
        }
      }
      setMatchFornitori(nextMap);
      // Persisti nella cache localStorage così il flag "ignorato" sopravvive al reload (TTL 2h)
      saveCache(fatture, nextMap, filtroDataDa, filtroDataA, lastCachedAt || Date.now());
      toast.success(`${nomeFornitore} non comparirà più tra le fatture SDI`);
    } catch (err) {
      console.error("Errore blocklist fornitore:", err);
      toast.error(err.response?.data?.detail || "Impossibile ignorare il fornitore");
    } finally {
      setIgnorandoInvoiceId(null);
    }
  };

  // Ripristina un fornitore ignorato (toggle locale + chiama API per pulizia)
  const handleRipristinaFornitore = async (fattura, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      // Cerca la voce in blocklist e cancellala
      const listRes = await axios.get(`${API}/sdi/fornitori-ignorati`, { headers: getAuthHeaders() });
      const items = listRes.data.items || [];
      const pivaFatt = (fattura.sender?.vat_code || "").replace(/\s+/g, "").replace(/^IT/i, "");
      const nomeFatt = (fattura.sender?.name || "").trim().toUpperCase();
      const target = items.find(it =>
        (it.piva && pivaFatt && it.piva === pivaFatt) ||
        (it.nome_norm && it.nome_norm === nomeFatt)
      );
      if (target) {
        await axios.delete(`${API}/sdi/fornitori-ignorati/${target.id}`, { headers: getAuthHeaders() });
      }
      // aggiorna stato locale
      const pivaTarget = pivaFatt;
      const nomeTarget = (fattura.sender?.name || "").trim().toLowerCase();
      const nextMap = { ...matchFornitori };
      for (const f of fatture) {
        const fPiva = (f.sender?.vat_code || "").replace(/\s+/g, "").replace(/^IT/i, "");
        const matchPiva = pivaTarget && fPiva === pivaTarget;
        const matchNome = !pivaTarget && nomeTarget && (f.sender?.name || "").trim().toLowerCase() === nomeTarget;
        if (matchPiva || matchNome) {
          nextMap[f.id] = { ...(nextMap[f.id] || {}), ignored: false };
        }
      }
      setMatchFornitori(nextMap);
      // Persisti nella cache localStorage così il ripristino sopravvive al reload (TTL 2h)
      saveCache(fatture, nextMap, filtroDataDa, filtroDataA, lastCachedAt || Date.now());
      toast.success(`${fattura.sender?.name || "Fornitore"} ripristinato`);
    } catch (err) {
      console.error("Errore ripristino fornitore:", err);
      toast.error("Impossibile ripristinare il fornitore");
    }
  };

  useEffect(() => {
    fetchSDIStatus();
  }, [fetchSDIStatus]);

  // Auto-fetch silenzioso al mount: se provider configurato e cache vuota
  // (attende che sdiStatus sia carico prima di procedere)
  useEffect(() => {
    if (!sdiStatus?.provider || !sdiStatus?.configured) return;
    if (hasSearched) return; // già caricato (da cache o click)
    // Fetch silenziosa senza toast per caricare la lista subito
    fetchFatture(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdiStatus?.provider, sdiStatus?.configured]);

  // Filtra fatture localmente
  const fattureFiltrate = fatture.filter(f => {
    const match = matchFornitori[f.id];
    const categoria = match?.categoria;
    const isIgnored = match?.ignored;
    const isImported = match?.imported;
    // Nasconde fatture già importate (a meno del toggle)
    if (!mostraImportate && isImported) {
      return false;
    }
    // Nasconde fornitori in blocklist (a meno del toggle)
    if (!mostraIgnorati && isIgnored) {
      return false;
    }
    // Filtro categoria: nascondi servizi/utenze a meno del toggle
    if (!mostraServizi && categoria === "servizi") {
      return false;
    }
    if (search) {
      const searchLower = search.toLowerCase();
      const matchSender = f.sender?.name?.toLowerCase().includes(searchLower);
      const matchNumber = f.document_number?.toLowerCase().includes(searchLower);
      if (!matchSender && !matchNumber) return false;
    }
    return true;
  });

  // Conta fatture servizi nascoste (escluse anche da già importate/ignorate)
  const fattureServiziNascoste = !mostraServizi
    ? fatture.filter(f => {
        const m = matchFornitori[f.id];
        return m?.categoria === "servizi" && !m?.ignored && !m?.imported;
      }).length
    : 0;

  // Conta fornitori unici ignorati nascosti
  const fattureIgnorateNascoste = !mostraIgnorati
    ? fatture.filter(f => matchFornitori[f.id]?.ignored && !matchFornitori[f.id]?.imported).length
    : 0;

  // Conta fatture già importate nascoste
  const fattureImportateNascoste = !mostraImportate
    ? fatture.filter(f => matchFornitori[f.id]?.imported).length
    : 0;

  // Badge categoria fornitore
  const renderCategoriaBadge = (categoria, matchMethod) => {
    if (categoria === "food") {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium" data-testid="badge-categoria-food">
          <Utensils className="w-3 h-3" /> Food & Beverage
        </span>
      );
    }
    if (categoria === "packaging") {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium" data-testid="badge-categoria-packaging">
          <Box className="w-3 h-3" /> Attrezzature & Packaging
        </span>
      );
    }
    if (categoria === "servizi") {
      const isAuto = matchMethod === "whitelist";
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium" data-testid="badge-categoria-servizi">
          <Wrench className="w-3 h-3" /> Servizi & Utenze{isAuto ? " (auto)" : ""}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium border border-amber-200" data-testid="badge-categoria-sconosciuto">
        <HelpCircle className="w-3 h-3" /> Fornitore nuovo
      </span>
    );
  };

  // Formatta importo
  const formatEuro = (val) => {
    if (val === null || val === undefined) return "—";
    return `€ ${Number(val).toFixed(2)}`;
  };

  // Formatta data
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-gray-500">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="import-fatture-sdi-page">
      {/* Header */}
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Fatture d'acquisto
            </h1>
            <p className="text-muted-foreground mt-1">
              Importa le fatture dei fornitori da {sdiStatus?.service || "provider configurato"}
            </p>
          </div>
          {sdiStatus?.provider && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/acquisti/config-fatture")}
              className="gap-1.5 rounded-xl"
              data-testid="header-config-btn"
            >
              <Settings className="w-4 h-4" />
              Impostazioni
            </Button>
          )}
        </div>
      )}

      {/* Status Card */}
      {!sdiStatus?.configured && !sdiStatus?.provider ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-amber-100 shrink-0">
              <Settings className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-foreground">Nessun provider configurato</div>
              <div className="text-sm text-muted-foreground">
                {sdiStatus?.message || "Configura un provider per importare le fatture elettroniche."}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-100 rounded-xl shrink-0"
              onClick={() => navigate("/acquisti/config-fatture")}
              data-testid="go-to-config-btn"
            >
              <Settings className="w-4 h-4" />
              Configura
            </Button>
          </div>
        </div>
      ) : (
        <div className={`rounded-2xl border p-4 ${
          sdiStatus?.mode === "sandbox" ? "bg-amber-50 border-amber-200" :
          sdiStatus?.configured ? "bg-emerald-50 border-emerald-200" :
          "bg-red-50 border-red-200"
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl shrink-0 ${sdiStatus?.configured ? "bg-emerald-100" : "bg-red-100"}`}>
              <Info className={`w-5 h-5 ${sdiStatus?.configured ? "text-emerald-600" : "text-red-600"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-foreground truncate">
                {sdiStatus?.service || "Provider SDI"}
              </div>
              <div className="text-sm text-muted-foreground">
                {sdiStatus?.configured
                  ? (sdiStatus?.message || "Connesso")
                  : (sdiStatus?.message || "Errore di connessione — verifica le credenziali")
                }
              </div>
            </div>
            {sdiStatus?.provider && (
              <Badge className={`shrink-0 ${sdiStatus?.configured ? "bg-emerald-100 text-emerald-700 border-0" : "bg-red-100 text-red-700 border-0"}`}>
                {sdiStatus?.provider === "fatture_in_cloud" ? "FIC" : sdiStatus?.provider?.toUpperCase()}
              </Badge>
            )}
            {sdiStatus?.provider && (
              <Button
                variant="outline"
                size="sm"
                className={`gap-1.5 shrink-0 rounded-xl ${
                  sdiStatus?.configured
                    ? "border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                    : "border-red-300 text-red-700 hover:bg-red-50"
                }`}
                onClick={() => navigate("/acquisti/config-fatture")}
                data-testid="modify-fic-connection-btn"
              >
                <Settings className="w-4 h-4" />
                {sdiStatus?.configured ? "Modifica" : "Riconnetti"}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Filtri e Carica */}
      {sdiStatus?.provider && (
        <div className="bg-muted/30 border border-border rounded-2xl p-4 space-y-3" data-testid="filtri-card">

            {/* Banner cache */}
            {lastCachedAt && !loading && (
              <div className="flex items-center justify-between text-xs text-muted-foreground bg-background border border-border/60 rounded-xl px-3 py-2">
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  Dati aggiornati {formatRelativeTime(lastCachedAt)}
                </span>
                <button
                  onClick={() => { setPage(1); fetchFatture(false); }}
                  disabled={loading || refreshing}
                  className="flex items-center gap-1 text-primary hover:text-primary/80 font-medium transition-colors disabled:opacity-50"
                  data-testid="aggiorna-rapido-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                  Aggiorna
                </button>
              </div>
            )}

            {/* Preset rapidi — pill stile Ordini */}
            <div className="flex flex-wrap gap-2">
              {[
                { id: "mese_corrente", label: "Questo mese" },
                { id: "mese_scorso",   label: "Mese scorso" },
                { id: "trimestre",     label: "Ultimi 3 mesi" },
                { id: "anno",          label: "Quest'anno" },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => applicaPreset(p.id)}
                  className={`h-9 px-4 rounded-2xl text-sm font-medium border transition-all ${
                    presetAttivo === p.id
                      ? "bg-primary text-white border-primary"
                      : "bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                  }`}
                  data-testid={`preset-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Range mese + bottone Carica */}
            <div className="flex flex-col sm:flex-row items-end gap-3">
              <div className="flex-1 w-full">
                <label className="text-xs text-muted-foreground mb-1 block">Da (mese)</label>
                <DateInput
                  monthOnly
                  value={filtroDataDa.slice(0, 7)}
                  onChange={(v) => handleMeseDa(v)}
                  data-testid="filtro-data-da"
                />
              </div>
              <div className="flex-1 w-full">
                <label className="text-xs text-muted-foreground mb-1 block">A (mese)</label>
                <DateInput
                  monthOnly
                  value={filtroDataA.slice(0, 7)}
                  onChange={(v) => handleMeseA(v)}
                  data-testid="filtro-data-a"
                />
              </div>
              <Button
                data-testid="carica-fatture-btn"
                onClick={() => { setPage(1); fetchFatture(); }}
                disabled={loading || refreshing}
                className="gap-2 w-full sm:w-auto h-11 rounded-2xl shrink-0"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Carica Fatture
              </Button>
            </div>

            {/* Filtri secondari (dopo primo caricamento) */}
            {hasSearched && fatture.length > 0 && (
              <div className="space-y-3 pt-2 border-t border-border/50">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="filtro-ricerca"
                      placeholder="Cerca fornitore o numero..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10 h-11 rounded-xl"
                    />
                  </div>
                  <Select value={filtroStato} onValueChange={(val) => { setFiltroStato(val); }}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="filtro-stato">
                      <SelectValue placeholder="Stato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tutti">Tutti gli stati</SelectItem>
                      <SelectItem value="NEW">Nuove</SelectItem>
                      <SelectItem value="RECEIVED">Ricevute</SelectItem>
                      <SelectItem value="DONE">Completate</SelectItem>
                      <SelectItem value="ERROR">Con errori</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(fattureImportateNascoste > 0 || mostraImportate) && (
                  <button type="button" data-testid="toggle-mostra-importate"
                    onClick={() => setMostraImportate(v => !v)}
                    className="flex items-center gap-2 text-xs text-emerald-700 hover:text-emerald-800 transition-colors"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {mostraImportate
                      ? "Nascondi le fatture già importate"
                      : `Mostra anche ${fattureImportateNascoste} fattur${fattureImportateNascoste === 1 ? "a" : "e"} già importat${fattureImportateNascoste === 1 ? "a" : "e"}`
                    }
                  </button>
                )}
                {(fattureServiziNascoste > 0 || mostraServizi) && (
                  <button type="button" data-testid="toggle-mostra-servizi"
                    onClick={() => setMostraServizi(v => !v)}
                    className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Wrench className="w-3.5 h-3.5" />
                    {mostraServizi
                      ? "Nascondi fatture di servizio/utenze"
                      : `Mostra anche ${fattureServiziNascoste} fattur${fattureServiziNascoste === 1 ? "a" : "e"} di servizio/utenze`
                    }
                  </button>
                )}
                {(fattureIgnorateNascoste > 0 || mostraIgnorati) && (
                  <button type="button" data-testid="toggle-mostra-ignorati"
                    onClick={() => setMostraIgnorati(v => !v)}
                    className="flex items-center gap-2 text-xs text-muted-foreground hover:text-rose-600 transition-colors"
                  >
                    <Ban className="w-3.5 h-3.5" />
                    {mostraIgnorati
                      ? "Nascondi di nuovo i fornitori ignorati"
                      : `Mostra anche ${fattureIgnorateNascoste} fattur${fattureIgnorateNascoste === 1 ? "a" : "e"} di fornitori ignorati`
                    }
                  </button>
                )}
              </div>
            )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
          <span className="text-muted-foreground">Caricamento fatture...</span>
        </div>
      ) : fattureFiltrate.length === 0 ? (
        <div className="bg-muted/30 border border-border rounded-2xl py-12 text-center px-4">
          {!sdiStatus?.provider ? (
            <>
              <FileWarning className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Nessun provider configurato</h3>
              <p className="text-sm text-muted-foreground mb-4">Configura un provider nelle impostazioni per importare le fatture.</p>
              <Button variant="outline" onClick={() => navigate("/acquisti/config-fatture")} className="gap-2 rounded-xl" data-testid="empty-go-to-config-btn">
                <Settings className="w-4 h-4" />Configura Provider
              </Button>
            </>
          ) : !hasSearched ? (
            <>
              <Calendar className="w-12 h-12 mx-auto text-primary/30 mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Seleziona un periodo e carica le fatture</h3>
              <p className="text-sm text-muted-foreground mb-4">Usa i filtri data sopra per scegliere il periodo, poi clicca "Carica Fatture".</p>
              <Button onClick={() => { setPage(1); fetchFatture(); }} disabled={loading} className="gap-2 rounded-xl" data-testid="empty-carica-btn">
                <Download className="w-4 h-4" />Carica Fatture
              </Button>
            </>
          ) : (
            <>
              <FileWarning className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Nessuna fattura trovata</h3>
              <p className="text-sm text-muted-foreground">Non ci sono fatture per il periodo selezionato.</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {fattureFiltrate.map((fattura) => {
            const statoInfo = STATI_FATTURA[fattura.state] || STATI_FATTURA.NEW;
            const StatoIcon = statoInfo.icon;
            const tipoDoc = TIPI_DOCUMENTO[fattura.document_type] || "Documento";
            const match = matchFornitori[fattura.id];
            const categoria = match?.categoria;
            const isServizio = categoria === "servizi";
            const isIgnored = match?.ignored;
            const isImported = match?.imported;
            const isParziale = match?.parziale;
            const righeRimanenti = match?.righe_rimanenti || 0;
            const isBusy = ignorandoInvoiceId === fattura.id;
            const isClickabile = !isServizio && !isIgnored && !isImported;

            return (
              <div
                key={fattura.id}
                className={`bg-card border border-border rounded-2xl p-4 transition-shadow ${isClickabile ? "hover:shadow-md cursor-pointer" : "opacity-60"}`}
                onClick={() => {
                  if (isImported) { toast.info("Questa fattura è già stata importata."); return; }
                  if (isIgnored) { toast.info("Questo fornitore è ignorato. Ripristinalo per importarne le fatture."); return; }
                  if (isServizio) { toast.info("Spesa di servizio: registrala dall'estratto conto bancario."); return; }
                  fetchFatturaDetail(fattura.id);
                }}
                data-testid={`fattura-card-${fattura.id}`}
              >
                <div className="flex items-center gap-4">
                  {/* Icona */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isImported ? "bg-emerald-50" : (isServizio || isIgnored ? "bg-muted" : "bg-primary/10")}`}>
                    {isImported ? (
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                    ) : (
                      <Building2 className={`w-6 h-6 ${isServizio || isIgnored ? "text-muted-foreground" : "text-primary"}`} />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-bold text-foreground truncate">
                        {fattura.sender?.name || "Fornitore sconosciuto"}
                      </h3>
                      {!isImported && (
                        <Badge className={statoInfo.color}>
                          <StatoIcon className="w-3 h-3 mr-1" />{statoInfo.label}
                        </Badge>
                      )}
                      {renderCategoriaBadge(categoria, match?.match_method)}
                      {isImported && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium" data-testid="badge-importata">
                          <CheckCircle2 className="w-3 h-3" /> Già importata
                        </span>
                      )}
                      {isIgnored && !isImported && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-medium" data-testid="badge-ignorato">
                          <Ban className="w-3 h-3" /> Ignorato
                        </span>
                      )}
                      {isParziale && !isImported && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid="badge-da-completare">
                          <AlertTriangle className="w-3 h-3" /> Da completare{righeRimanenti > 0 ? ` (${righeRimanenti} ${righeRimanenti === 1 ? "riga" : "righe"})` : ""}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{fattura.document_number}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(fattura.issue_date)}</span>
                      <span className="px-2 py-0.5 bg-muted rounded-lg text-xs">{tipoDoc}</span>
                      {fattura.lines?.length > 0 && (
                        <span className="flex items-center gap-1"><Package className="w-3 h-3" />{fattura.lines.length} prodotti</span>
                      )}
                    </div>
                    {isServizio && !isIgnored && !isImported && (
                      <p className="text-xs text-muted-foreground italic mt-1">Spesa di servizio — registrala dall'estratto conto</p>
                    )}
                    {isIgnored && !isImported && (
                      <p className="text-xs text-rose-600 italic mt-1">Fornitore in blocklist — le sue fatture sono nascoste di default</p>
                    )}
                  </div>

                  {/* Importo */}
                  <div className="text-right shrink-0">
                    <p className="text-lg font-bold text-foreground">{formatEuro(fattura.total_amount)}</p>
                    <p className="text-xs text-muted-foreground">IVA: {formatEuro(fattura.total_vat)}</p>
                  </div>

                  {/* Azioni */}
                  {!isImported && (
                    isIgnored ? (
                      <Button variant="outline" size="sm" onClick={(e) => handleRipristinaFornitore(fattura, e)}
                        className="shrink-0 text-amber-600 border-amber-300 hover:bg-amber-50 rounded-xl"
                        data-testid={`ripristina-fornitore-btn-${fattura.id}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />Ripristina
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" disabled={isBusy} onClick={(e) => handleIgnoraFornitore(fattura, e)}
                        className="shrink-0 text-rose-600 border-rose-300 hover:bg-rose-50 rounded-xl"
                        data-testid={`ignora-fornitore-btn-${fattura.id}`}>
                        {isBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Ban className="w-3.5 h-3.5 mr-1" />}
                        Non importare più
                      </Button>
                    )
                  )}
                  {isClickabile && <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog Dettaglio Fattura */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-500" />
              Dettaglio Fattura {fatturaDetail?.document_number}
            </DialogTitle>
            <DialogDescription>
              {fatturaDetail?.sender?.name} - {formatDate(fatturaDetail?.issue_date)}
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
          ) : fatturaDetail ? (
            <div className="space-y-6">
              {/* Info Generali */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/50 border border-border rounded-xl p-4">
                  <h4 className="font-medium text-muted-foreground text-sm mb-2">Fornitore</h4>
                  <p className="font-bold text-foreground">{fatturaDetail.sender?.name}</p>
                  <p className="text-sm text-muted-foreground">P.IVA: {fatturaDetail.sender?.vat_code}</p>
                  {fatturaDetail.sender?.street && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {fatturaDetail.sender.street}, {fatturaDetail.sender.city} ({fatturaDetail.sender.province})
                    </p>
                  )}
                </div>
                <div className="bg-muted/50 border border-border rounded-xl p-4">
                  <h4 className="font-medium text-muted-foreground text-sm mb-2">Totali</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Imponibile:</span>
                      <span className="font-medium text-foreground">{formatEuro(fatturaDetail.total_taxable)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">IVA:</span>
                      <span className="font-medium text-foreground">{formatEuro(fatturaDetail.total_vat)}</span>
                    </div>
                    <div className="flex justify-between border-t border-border pt-1">
                      <span className="font-semibold text-foreground">Totale:</span>
                      <span className="font-bold text-lg text-primary">{formatEuro(fatturaDetail.total_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Righe Prodotti */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Prodotti ({fatturaDetail.lines?.length || 0})
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-600">Descrizione</th>
                        <th className="text-right p-3 font-medium text-gray-600">Qtà</th>
                        <th className="text-right p-3 font-medium text-gray-600">Prezzo</th>
                        <th className="text-right p-3 font-medium text-gray-600">IVA</th>
                        <th className="text-right p-3 font-medium text-gray-600">Totale</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {fatturaDetail.lines?.map((line, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-3">
                            <p className="font-medium text-gray-900">{line.description}</p>
                            {line.unit_of_measure && (
                              <p className="text-xs text-gray-500">UM: {line.unit_of_measure}</p>
                            )}
                          </td>
                          <td className="p-3 text-right text-gray-700">{line.quantity}</td>
                          <td className="p-3 text-right text-gray-700">{formatEuro(line.unit_price)}</td>
                          <td className="p-3 text-right text-gray-700">{line.vat_rate}%</td>
                          <td className="p-3 text-right font-medium text-gray-900">{formatEuro(line.total_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Info Pagamento */}
              {(fatturaDetail.payment_terms || fatturaDetail.payment_due_date) && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <h4 className="font-medium text-blue-800 text-sm mb-2">Pagamento</h4>
                  <div className="flex gap-6">
                    {fatturaDetail.payment_due_date && (
                      <div>
                        <span className="text-sm text-blue-600">Scadenza: </span>
                        <span className="font-medium text-blue-900">{formatDate(fatturaDetail.payment_due_date)}</span>
                      </div>
                    )}
                    {fatturaDetail.payment_terms && (
                      <div>
                        <span className="text-sm text-blue-600">Modalità: </span>
                        <span className="font-medium text-blue-900">
                          {fatturaDetail.payment_terms === "MP05" ? "Bonifico" : fatturaDetail.payment_terms}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Chiudi
            </Button>
            <Button 
              onClick={() => {
                setDetailDialogOpen(false);
                handleProcessFattura(fatturaDetail);
              }}
              className="bg-indigo-500 hover:bg-indigo-600"
            >
              <Download className="w-4 h-4 mr-2" />
              Importa Fattura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportFattureSDI;
